/**
 * Validación en el borde de las respuestas de la API v2.
 *
 * POR QUÉ EXISTE. El tipo generado por el SDK dice qué DEBERÍA llegar, no qué llega.
 * Cuando el servidor responde 200 con otra cosa —un HTML de proxy, un despliegue a
 * medias, un error mal formado, un contrato que se movió sin regenerar el cliente—
 * TypeScript no se entera y el cuerpo entra a la pantalla como si fuera bueno.
 *
 * El síntoma aparece LEJOS de la causa: un `data.pagination` indefinido acá, un
 * `summary.byStatus` allá, cada uno tumbando la vista al error boundary por su lado.
 * Parchar esos accesos con optional chaining es whack-a-mole: mueve la explosión al
 * siguiente consumidor en vez de matarla.
 *
 * Validar contra el schema del SSOT (`@idc-forms/schemas/api-v2/*`) convierte todo eso
 * en UN error de query, que las pantallas ya saben mostrar (`DataTableErrorState`, con
 * su botón de reintentar), en el punto donde el dato entra a la aplicación.
 *
 * QUÉ SE VALIDA Y QUÉ NO. Solo las LECTURAS. Una escritura que responde 200 ya ocurrió:
 * hacerla fallar acá porque el cuerpo de vuelta no cuadra le mentiría al usuario sobre
 * algo que sí se guardó. En una lectura, en cambio, fallar es exactamente lo correcto —
 * no hay nada que mostrar y reintentar es la salida natural.
 *
 * ── ☠️ Y POR QUÉ EL RECHAZO TIENE QUE DECIR QUÉ CAMPO ───────────────────────
 *
 * El portero funcionaba y era MUDO. Un `ZodError` viajaba tal cual hasta la query, la
 * pantalla dibujaba su frase fija —«No se pudo cargar el expediente.»— y ahí terminaba
 * todo: las queries no pasan por `handleMutationError`, así que a Sentry no llegaba
 * NADA. El 2026-08-24 el expediente de un cliente recién creado no cargaba en staging y
 * los logs del servidor no tenían una sola línea del pedido: había respondido 200 y el
 * rechazo era de este lado, invisible. Sin el campo culpable no hay diagnóstico posible
 * — sólo adivinanza sobre un contrato de 20 campos anidados.
 *
 * Así que acá el error se NOMBRA: las rutas que fallaron van al mensaje, al `console` en
 * desarrollo y a Sentry con la etiqueta `contract`. Cuando el contrato se mueva otra vez,
 * la respuesta va a estar escrita en el reporte, no escondida en un `parse`.
 */

import * as Sentry from '@sentry/react'

/** Lo mínimo que se necesita de un schema Zod: poder parsear. Evita atar el borde a la versión de zod. */
type Parseable = { parse: (value: unknown) => unknown }

/**
 * El rechazo del portero, con NOMBRE propio.
 *
 * Es una clase y no un `Error` pelado para que `getErrorMessage` pueda mostrar su mensaje
 * tal cual sin abrirle la puerta a CUALQUIER `Error` de JavaScript: un
 * «Cannot read properties of undefined» en la cara del asesor no ayuda a nadie. Acá el
 * mensaje se escribe para ser leído y pegado en un reporte.
 */
export class ContractError extends Error {
  readonly issues: string[]

  constructor(message: string, issues: string[], cause: unknown) {
    super(message, { cause })
    this.name = 'ContractError'
    this.issues = issues
  }
}

/** El detalle de un `ZodError`, leído sin importar zod ni atarse a su versión. */
type IssueLike = { path?: unknown; message?: unknown }

/**
 * Las RUTAS que reprobaron, en la forma en que el schema las nombra
 * (`applicant.name.firstName: Required`). Es el dato que convierte «no cargó» en «falta
 * este campo», y viaja tal cual: traducirlo sería inventar un diccionario que se
 * desincroniza con el schema al primer cambio.
 */
function failedPaths(error: unknown): string[] {
  const issues = (error as { issues?: unknown })?.issues
  if (!Array.isArray(issues)) return []

  return issues.slice(0, 12).map((issue: IssueLike) => {
    const path = Array.isArray(issue.path) ? issue.path.join('.') : ''
    const message = typeof issue.message === 'string' ? issue.message : 'inválido'
    return path ? `${path}: ${message}` : message
  })
}

/**
 * Verifica el valor contra el schema y devuelve el ORIGINAL, tipado por el SDK.
 *
 * Se devuelve el valor de entrada y no el resultado del `parse` a propósito: los schemas
 * del SSOT aplican marcas de tipo (`Instant`, `LocalDate`) que el SDK aplana a `string`,
 * y arrastrarlas hasta la tabla no aporta nada. Acá el schema es un portero, no un mapper.
 *
 * `context` nombra la lectura —`'portal.dossier'`, `'clients.detail'`— para que el reporte
 * diga QUÉ pantalla se quedó sin datos y no sólo qué campo falló. Es opcional: un borde que
 * todavía no lo pasa sigue reportando, sin la etiqueta.
 */
export function assertShape<T>(schema: Parseable, value: T, context?: string): T {
  try {
    schema.parse(value)
  } catch (error) {
    const paths = failedPaths(error)
    const etiqueta = context ? `${context}: ` : ''
    const detalle = paths.length > 0 ? paths.join(' · ') : 'el cuerpo no cumple el contrato'

    // En desarrollo el cuerpo entero vale más que cualquier resumen: es lo que se pega en
    // el reporte del contrato.
    if (import.meta.env.DEV) {
      console.error(`[contrato] ${etiqueta}${detalle}`, { value, error })
    }

    Sentry.withScope((scope) => {
      scope.setLevel('error')
      scope.setTag('kind', 'contract')
      if (context) scope.setTag('contract', context)
      scope.setExtra('issues', paths)
      Sentry.captureException(error)
    })

    // Se reemplaza el `ZodError` por un error cuyo MENSAJE ya es el diagnóstico: es lo que
    // termina en `getErrorMessage`, y por lo tanto lo que puede leer quien reporta el fallo.
    throw new ContractError(
      `La respuesta del servidor no cumple el contrato — ${etiqueta}${detalle}`,
      paths,
      error
    )
  }

  return value
}
